import type { TFunction } from 'i18next';

/**
 * "2 hrs 30 mins". Both halves pluralise, and English's rule is not Spanish's,
 * so each is its own `_one`/`_other` pair and the join between them is a key
 * rather than a literal space. The two counts never have to agree with one
 * another, which is why they can be two keys at all.
 */
export function formatDuration(t: TFunction, durationMinutes: number): string {
    const hours: number = Math.floor(durationMinutes / 60);
    const minutes: number = durationMinutes % 60;

    const parts: string[] = [];
    if (hours > 0) {
        parts.push(t('shared.tasks.duration_hours', { count: hours }));
    }
    if (minutes > 0) {
        parts.push(t('shared.tasks.duration_minutes', { count: minutes }));
    }

    return parts.join(t('shared.tasks.duration_separator'));
}

/**
 * "N 51.50000° W 0.12000° (~15m accuracy)".
 *
 * `common/util/location`'s `toPrettyGpsCoordinates` and `toPrettyAccuracy`
 * build the same string, but they are English produced by a shared util that no
 * namespace owns — N/S/E/W are not the cardinals Spanish uses. This keys it
 * inside the module instead. The accuracy half is a whole second key rather
 * than a fragment, because a translator cannot place a clause that may not be
 * there.
 */
export function formatGpsCoordinates(
    t: TFunction,
    lat: number,
    lng: number,
    acc?: number | null
): string {
    const latitude =
        lat >= 0
            ? t('shared.tasks.latitude_north', {
                  value: Math.abs(lat).toFixed(5)
              })
            : t('shared.tasks.latitude_south', {
                  value: Math.abs(lat).toFixed(5)
              });
    const longitude =
        lng >= 0
            ? t('shared.tasks.longitude_east', {
                  value: Math.abs(lng).toFixed(5)
              })
            : t('shared.tasks.longitude_west', {
                  value: Math.abs(lng).toFixed(5)
              });

    if (!acc) {
        return t('shared.tasks.gps_coordinates_value', { latitude, longitude });
    }

    return t('shared.tasks.gps_coordinates_value_with_accuracy', {
        latitude,
        longitude,
        accuracy: t('shared.tasks.gps_accuracy', { value: acc.toFixed(0) })
    });
}

/**
 * "175 cm" or "5 ft 9 in" — the unit belongs to the sentence, not to code.
 *
 * Feet and inches are two independently-pluralised keys joined by a separator,
 * for the same reason `formatDuration` is: one key cannot carry two numbers
 * that pluralise separately, and Spanish needs `pie` or `pies`, and `pulgada`
 * or `pulgadas`, to agree with their own count.
 */
export function formatHeight(
    t: TFunction,
    value: number,
    unit: 'CM' | 'INCH'
): string {
    if (unit === 'CM') {
        return t('shared.tasks.height_cm', { value });
    }

    return [
        t('shared.tasks.height_feet', { count: Math.floor(value / 12) }),
        t('shared.tasks.height_inches', { count: value % 12 })
    ].join(t('shared.tasks.height_separator'));
}
